import type { PerspectiveMesh, Texture } from "pixi.js";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useScene } from "../hooks/useScene";
import {
    acquireOwnedImageAsset,
    getPlaceholderTexture,
} from "../imageOverlayAssets";
import { projectImageOverlayToScene } from "../imageOverlayGeometry";
import type { ImageOverlayMapObject } from "../types";

interface ImageOverlayObjectProps {
    item: ImageOverlayMapObject;
    selected: boolean;
    foreground: boolean;
    order: number;
    layerItemCount: number;
}

interface ImageAssetRequest {
    imageUrl: string;
}
type SourceTextureState =
    | { request: ImageAssetRequest; status: "loaded"; texture: Texture }
    | { request: ImageAssetRequest; status: "unavailable" };

function useImageOverlayTexture(
    item: ImageOverlayMapObject,
    selected: boolean,
): { texture: Texture; sourceLoaded: boolean } {
    const [sourceTexture, setSourceTexture] =
        useState<SourceTextureState | null>(null);
    const [detailTexture, setDetailTexture] = useState<{
        request: ImageAssetRequest;
        texture: Texture;
    } | null>(null);
    const { source } = item;
    const sourceImageUrl = source.status === "image" ? source.imageUrl : null;
    // Request identity distinguishes separate ownership periods, including A -> B -> A.
    const sourceRequest = useMemo<ImageAssetRequest | null>(
        () => (sourceImageUrl === null ? null : { imageUrl: sourceImageUrl }),
        [sourceImageUrl],
    );

    useEffect(() => {
        if (sourceRequest === null) return;

        let active = true;
        const asset = acquireOwnedImageAsset(sourceRequest.imageUrl);

        void asset.texturePromise
            .then((loadedTexture) => {
                if (!active) return;

                setSourceTexture({
                    request: sourceRequest,
                    status: "loaded",
                    texture: loadedTexture,
                });
            })
            .catch((error: unknown) => {
                if (!active) return;

                console.error(
                    `Image overlay load failed for ${item.id}`,
                    error,
                );
                setSourceTexture({
                    request: sourceRequest,
                    status: "unavailable",
                });
            });

        return () => {
            active = false;
            asset.release();
        };
    }, [item.id, sourceRequest]);

    const sourceLoaded =
        sourceRequest !== null &&
        sourceTexture?.request === sourceRequest &&
        sourceTexture.status === "loaded";
    const activeDetailImageUrl =
        selected &&
        sourceLoaded &&
        typeof item.detailImageUrl === "string" &&
        item.detailImageUrl.length > 0
            ? item.detailImageUrl
            : null;
    const detailRequest = useMemo<ImageAssetRequest | null>(
        () =>
            activeDetailImageUrl === null
                ? null
                : { imageUrl: activeDetailImageUrl },
        [activeDetailImageUrl],
    );

    useEffect(() => {
        if (detailRequest === null) return;

        let active = true;
        const asset = acquireOwnedImageAsset(detailRequest.imageUrl);

        void asset.texturePromise
            .then((loadedTexture) => {
                if (!active) return;

                setDetailTexture({
                    request: detailRequest,
                    texture: loadedTexture,
                });
            })
            .catch((error: unknown) => {
                if (!active) return;

                console.error(
                    `Image overlay detail load failed for ${item.id}`,
                    error,
                );
            });

        return () => {
            active = false;
            asset.release();
        };
    }, [detailRequest, item.id]);

    if (source.status !== "image") {
        return {
            texture: getPlaceholderTexture(source.status),
            sourceLoaded: false,
        };
    }

    if (sourceTexture?.request !== sourceRequest) {
        return {
            texture: getPlaceholderTexture("loading"),
            sourceLoaded: false,
        };
    }

    if (sourceTexture.status === "unavailable") {
        return {
            texture: getPlaceholderTexture("unavailable"),
            sourceLoaded: false,
        };
    }

    return {
        texture:
            detailRequest !== null && detailTexture?.request === detailRequest
                ? detailTexture.texture
                : sourceTexture.texture,
        sourceLoaded: true,
    };
}

const ImageOverlayObject = ({
    item,
    selected,
    foreground,
    order,
    layerItemCount,
}: ImageOverlayObjectProps) => {
    const { coordinates } = useScene();
    const sceneGeometry = useMemo(
        () => projectImageOverlayToScene(item.corners, coordinates),
        [coordinates, item.corners],
    );
    const { texture, sourceLoaded } = useImageOverlayTexture(item, selected);

    const setMeshRef = useCallback(
        (mesh: PerspectiveMesh | null) => {
            if (!mesh || !sceneGeometry) return;

            mesh.setCorners(
                sceneGeometry.sceneCorners[0].x,
                sceneGeometry.sceneCorners[0].y,
                sceneGeometry.sceneCorners[1].x,
                sceneGeometry.sceneCorners[1].y,
                sceneGeometry.sceneCorners[2].x,
                sceneGeometry.sceneCorners[2].y,
                sceneGeometry.sceneCorners[3].x,
                sceneGeometry.sceneCorners[3].y,
            );
        },
        [sceneGeometry],
    );

    if (!sceneGeometry) return null;

    return (
        <pixiContainer
            label="image-overlay-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={sceneGeometry.cullBounds}
            zIndex={
                foreground
                    ? layerItemCount * 2
                    : sourceLoaded
                      ? layerItemCount + order
                      : order
            }
            eventMode="none"
        >
            <pixiPerspectiveMesh
                ref={setMeshRef}
                texture={texture}
                verticesX={2}
                verticesY={2}
                eventMode="none"
            />
        </pixiContainer>
    );
};

const haveSameRenderInputs = (
    previous: ImageOverlayObjectProps,
    next: ImageOverlayObjectProps,
): boolean =>
    previous.item.id === next.item.id &&
    previous.item.corners === next.item.corners &&
    previous.item.source === next.item.source &&
    previous.item.detailImageUrl === next.item.detailImageUrl &&
    previous.selected === next.selected &&
    previous.foreground === next.foreground &&
    previous.order === next.order &&
    previous.layerItemCount === next.layerItemCount;

export default memo(ImageOverlayObject, haveSameRenderInputs);
